import {BuyBonusButtonConstants} from '@gongxigames/framework/slot/modules/buyBonus/buyBonusButton/BuyBonusButtonConstants';
import {KeypadView} from '@gongxigames/framework/slot/modules/keypad/KeypadView';
import {Container, Sprite, Text, Texture} from 'pixi.js';
import {isEnabledRubyCoins} from '@gongxigames/framework/slot/modules/buyBonus/isEnabledRubyCoins';
import {
    AdvancedText, AnimationProperty,
    Button,
    CoreDI,
    CurrencyFormatter,
    DeviceType,
    IAdvancedTextConfig, inject,
    LanguageService,
    ModuleIsEnabledFilter, Spine
} from '@gongxigames/framework/core';
import {GlobalEventsMapForBackwardCompatibility} from '@gongxigames/framework/slot/events';
import {DI} from '@gongxigames/framework/slot/configs/DI';
import {LAYERS} from '@gongxigames/framework/slot/configs/Layers';
import {SlotSettings} from '@gongxigames/framework/slot/configs/SlotSettings';
import {GameConfig, ILogic} from '@gongxigames/framework/slot/types';
import {View} from '@gongxigames/framework/slot/fragments/module/view';

export class BuyBonusButtonView extends View<GlobalEventsMapForBackwardCompatibility> {
    public static readonly NAME: symbol = Symbol('BuyButtonView');
    public static readonly isEnabled: ModuleIsEnabledFilter = isEnabledRubyCoins;
    public static layer = LAYERS.KEYPAD;
    public static platform: DeviceType = DeviceType.DESKTOP;
    public static readonly BUTTON_CLICKED: symbol = Symbol('BUTTON_CLICKED');

    @inject(CoreDI.CurrencyFormatter)
    protected _currencyFormatter!: CurrencyFormatter;

    @inject(CoreDI.GameConfig)
    protected _gameConfig!: GameConfig;

    @inject(CoreDI.LanguageService)
    protected _languageService!: LanguageService;

    @inject(DI.Logic)
    protected _logic!: ILogic;

    protected _buyButton!: Button;

    protected _advancedTexts: IAdvancedTextConfig[] = [];
    protected _textsConfigName!: string;

    protected _spineContainer!: Container;
    protected _spine!: Spine;

    protected _buttonContainer!: Container;
    protected _buttonSprite!: Sprite;
    protected _textContainer!: Sprite;
    protected _buyTXT!: AdvancedText;
    protected _bonusTXT!: AdvancedText;

    public init(): void {
        super.init();

        if (this._gameConfig.buyButtonTXT) {
            this._advancedTexts = this._gameConfig.buyButtonTXT;
        }

        this.initGraphicsElements();
        this.mainSprite.addChild(this.container);
    }

    public initGraphicsElements(): void {
        this.initButton();
        this.initControls();
        this.initTexts();
        this.initSpine();
        this.initSpineTexts();
    }

    public initButton(): void {
        this._buttonSprite = this.mainSprite.getChildByName<Sprite>('buyButton');
    }

    protected initControls(): void {
        this._buttonContainer = new Container;
        this._buyButton = this._buttonSprite.getChildByName<Button>('button');
        this._buyButton.callback = this.onBuyBonusButtonClicked.bind(this);
        this._buttonContainer.addChild(this._buyButton);
        this._textContainer = this._buttonSprite.getChildByName<Sprite>('text');

        this.container.addChild(this._buttonContainer);
    }

    protected initSpine(): void {
        this._spineContainer = new Container();
        this._spine = this.objectBuilder.buildSpine(BuyBonusButtonConstants.SPINE_NAME);
        this._spine.x = this._buyButton.x + this._buyButton.getLocalBounds().width / 2;
        this._spine.y = this._buyButton.y + this._buyButton.getLocalBounds().height / 2;

        this._spineContainer.visible = false;
        this._spine.state.clearTracks();
        this._spine.skeleton.setToSetupPose();
        this._spine.start();
        this._spineContainer.addChild(this._spine);

        this.clearTexture('text_3');
        this.updateManager.add(this._spine);
        this.container.addChild(this._spineContainer);
    }

    protected clearTexture(slotName: string): void {
        const slotSprite = this._spine.skeleton.findSlot(slotName).currentSprite;

        slotSprite.texture = Texture.EMPTY;
        slotSprite.removeChildren();
    }

    protected onBuyBonusButtonClicked(): void {
        this.disableButton();
        this._buttonContainer.visible = false;
        this.playAnimation();
        this.soundPlayer.play('buttonclick');
        this.emit(BuyBonusButtonView.BUTTON_CLICKED);
    }

    public disableButton(): void {
        this._buyButton.disable();
    }

    public enableButton(): void {
        this._spineContainer.visible = false;
        this._buttonContainer.visible = true;
        this._buyButton.enable();
    }

    protected initTexts(): void {
        const {BUY_TEXT_ID, PLAY_TEXT_ID, BONUS_TEXT_ID} = BuyBonusButtonConstants;
        const ukRegulations = this.getSettings<SlotSettings>().get('ukRegulations');

        this._advancedTexts.forEach(data => {
            const buyTextId = ukRegulations ? PLAY_TEXT_ID : BUY_TEXT_ID;
            const buyTXTName = ukRegulations ? 'playText' : 'buyText';

            if (data.id === buyTextId) {
                const buyTXT = this._textContainer.getChildByName<Text>(buyTXTName);

                this._buyTXT = new AdvancedText(
                    this.objectBuilder,
                    this._languageService.get(data.id),
                    data.style,
                    this._screenManager.defaultViewportResolution
                );
                this._buyTXT.maxWidth = data.maxWidth;
                this._buyTXT.x = this._buyButton.x + this._buyButton.getLocalBounds().width / 2;
                this._buyTXT.y = buyTXT.y - this._buyTXT.height / 2;
                this._buttonContainer.addChild(this._buyTXT);
            } else if (data.id === BONUS_TEXT_ID) {
                const bonusTXT = this._textContainer.getChildByName<Text>('bonusText');

                this._bonusTXT = new AdvancedText(
                    this.objectBuilder,
                    this._languageService.get(data.id),
                    data.style,
                    this._screenManager.defaultViewportResolution
                );
                this._bonusTXT.maxWidth = data.maxWidth;
                this._bonusTXT.x = this._buyButton.x + this._buyButton.getLocalBounds().width / 2;
                this._bonusTXT.y = bonusTXT.y - this._bonusTXT.height / 2;
                this._buttonContainer.addChild(this._bonusTXT);
            }
        });

        this._textContainer.removeChildren();
    }

    protected initSpineTexts(): void {
        const {BUY_TEXT_ID, PLAY_TEXT_ID, BONUS_TEXT_ID} = BuyBonusButtonConstants;
        const buyTextId = this.getSettings<SlotSettings>().get('ukRegulations') ? PLAY_TEXT_ID : BUY_TEXT_ID;
        const advancedTexts = (this._gameConfig as any)[this._textsConfigName] as IAdvancedTextConfig[];

        if (advancedTexts) {
            this._advancedTexts = advancedTexts;
        }

        this._advancedTexts.forEach(data => {
            if (data.id === buyTextId || data.id === BONUS_TEXT_ID) {
                const config = {
                    text: this._languageService.get(data.id),
                    style: data.style
                };

                const advancedText = this._objectBuilder.buildAdvancedText(config);

                this.addTextToSlot(advancedText, data);
            }
        });
    }

    protected addTextToSlot(text: AdvancedText, data: IAdvancedTextConfig): void {
        this.clearTexture(data.slot);

        const slotSprite = this._spine.skeleton.findSlot(data.slot).currentSprite;
        const sprite = new Sprite();

        if (data.id === BuyBonusButtonConstants.BONUS_TEXT_ID) {
            text.y = -this._bonusTXT.height / 2;
        } else {
            text.y = -this._buyTXT.height / 2;
        }

        if (data) {
            text.maxWidth = data.maxWidth;

            if (data.position) {
                if (data.position.x) {
                    text.x += data.position.x;
                }

                if (data.position.y) {
                    text.y += data.position.y;
                }
            }
        }

        sprite.addChild(text);
        slotSprite.addChild(sprite);
    }

    public playAnimation(): void {
        const animProperty: AnimationProperty = {
            animationName: BuyBonusButtonConstants.CLICK_ANIMATION_NAME,
            trackIndex: 0,
            loop: false,
            listeners: {
                complete: () => {
                    this._spineContainer.visible = false;
                    this._buttonContainer.visible = true;
                }
            }
        };

        this._spineContainer.visible = true;
        this._spine.setAnimation(animProperty);
        this.container.alpha = 1;
    }

    protected get mainSprite(): Sprite {
        return this._installer.getComponent<KeypadView>(KeypadView.NAME).mainSprite;
    }
}
